/**
 * useResultDownload hook
 *
 * THE single place that answers "the job finished — how do we hand the file to
 * the user?". Three cases, one implementation:
 *
 *   - Desktop AND the artifact is already on this machine -> reveal it in
 *     Finder/Explorer. "Downloading" a file that is already on the user's disk
 *     is a lie: it re-fetches bytes over HTTP to write them somewhere else.
 *   - Desktop, no local path (the job ran in the cloud) -> resolve the URL to a
 *     Blob, convert to a data: URL, and hand it to the native save-file IPC
 *     bridge, avoiding Chromium's own download manager whose native "Save As"
 *     dialog can appear behind the main window.
 *   - Web -> downloadFile (server-side proxy + anchor-click download).
 *
 * Every result surface duplicated some subset of this branch, and each copy got
 * a different part wrong: the Dubbing tab offered an HTTP download for a local
 * ZIP and 404'd; Subtitles and Speech-to-Text anchor-clicked blobs straight
 * into Chromium's download manager; the Converter passed an http:// URL to a
 * filesystem-open IPC call, which silently does nothing.
 *
 * A result link is often a presigned S3 URL that dies an hour after it was
 * signed, while the result stays on screen for up to a day. A caller holding
 * such a link passes `renewUrl`, and the download uses the link the backend
 * signs at that moment, never the one held in state.
 *
 * On failure this reports the error. It used to `window.open(resultUrl)`, which
 * for a failing URL simply showed the user the raw JSON error body in a blank
 * window -- exactly the "download" the 2026-08-08 dubbing bug report attached a
 * screenshot of.
 *
 * @param resultUrl      - URL to download (may be null/undefined before generation).
 * @param filenamePrefix - Prefix for the saved filename; timestamp is appended automatically.
 *                         Example: "motion-control" -> "motion-control-1718000000000.mp4"
 * @param extension      - File extension without leading dot (default: "mp4").
 * @param options        - Optional local path (enables reveal-in-folder) and error sink.
 */

import { useState, useCallback } from 'react';
import { downloadFile, resolveUrlToBlob, blobToDataUrl } from '@/utils/downloadProxy';
import { saveMediaFile, openVideoFolder } from '@/utils/electronBridge';
import { isDesktopApp } from '@/utils/apiMode';

export interface UseResultDownloadOptions {
  /**
   * Absolute path of the artifact on THIS machine, when the job ran locally.
   * Must be a filesystem path — never an http(s) URL. Passing a URL here is a
   * real bug seen in the Converter: `shell.showItemInFolder("http://...")` is
   * a silent no-op, so the button did nothing at all.
   */
  localPath?: string | null;
  /** Called with a human-readable message when delivery fails. */
  onError?: (message: string) => void;
  /**
   * Resolve a freshly signed URL for the artifact; called right before the
   * download. Pass it when `resultUrl` is a presigned link that expires (it
   * re-reads the job, and the backend re-signs on every read). Omit for links
   * that cannot expire.
   */
  renewUrl?: () => Promise<string>;
}

export interface UseResultDownloadResult {
  /** Whether a download is currently in progress. */
  isDownloading: boolean;
  /** Call this to trigger a download of resultUrl (web proxy or Electron native save). */
  handleDownload: () => Promise<void>;
  /** Whether the app is running inside the Electron desktop shell. */
  isDesktop: boolean;
  /**
   * True when the UI should offer "Show in Folder" INSTEAD of a download —
   * desktop shell AND a real local file. `isDesktop` alone is not enough: a
   * desktop user running a cloud job has no local file and must still download.
   */
  showOpenFolder: boolean;
  /** Reveal the local artifact in Finder/Explorer. */
  handleOpenFolder: () => Promise<void>;
  /** Last delivery error, or null. */
  error: string | null;
}

function isFilesystemPath(candidate: string | null | undefined): candidate is string {
  return Boolean(candidate) && !/^(https?|blob|data):/i.test(candidate as string);
}

export function useResultDownload(
  resultUrl: string | null | undefined,
  filenamePrefix: string,
  extension: string = 'mp4',
  options: UseResultDownloadOptions = {}
): UseResultDownloadResult {
  const { localPath = null, onError, renewUrl } = options;
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDesktop = isDesktopApp();

  // Guard the path shape here so no caller can hand a URL to a filesystem call.
  const revealablePath = isFilesystemPath(localPath) ? localPath : null;
  const showOpenFolder = isDesktop && revealablePath !== null;

  const report = useCallback(
    (message: string) => {
      setError(message);
      if (onError) onError(message);
      else console.error(`[useResultDownload] ${message}`);
    },
    [onError]
  );

  const handleDownload = useCallback(async () => {
    if (!resultUrl || isDownloading) return;

    setIsDownloading(true);
    setError(null);
    try {
      const filename = `${filenamePrefix}-${Date.now()}.${extension}`;
      const url = renewUrl ? await renewUrl() : resultUrl;

      if (isDesktop) {
        const blob = await resolveUrlToBlob(url);
        const dataUrl = await blobToDataUrl(blob);
        const result = await saveMediaFile(dataUrl, filename);
        if (!result.success && !result.canceled) {
          throw new Error(result.error ?? 'Failed to save file');
        }
      } else {
        await downloadFile(url, filename);
      }
    } catch (err) {
      report(err instanceof Error ? err.message : String(err));
    } finally {
      setIsDownloading(false);
    }
  }, [resultUrl, renewUrl, isDownloading, filenamePrefix, extension, isDesktop, report]);

  const handleOpenFolder = useCallback(async () => {
    if (!revealablePath) {
      report('No local file path available - this file was generated in the cloud.');
      return;
    }
    const opened = await openVideoFolder(revealablePath);
    if (!opened) {
      report('Failed to open the folder. Please navigate there manually.');
    }
  }, [revealablePath, report]);

  return { isDownloading, handleDownload, isDesktop, showOpenFolder, handleOpenFolder, error };
}
